import React, { useEffect, useState, useMemo } from 'react';
import { Box, Drawer, makeStyles } from '@material-ui/core';
import Calendar from 'react-calendar';
import moment from 'moment';

import calendarImage from '@/assets/images/icon_calendar.svg';
import closeImage from '@/assets/images/icon_close.svg';
import { Schedule } from '@/types/Doctor';
import { useTranslate } from '@/locale';

import SelectableBox from './SelectableBox';

const useStyles = makeStyles({
  dateTitle: {
    fontWeight: 400,
    fontSize: 12,
    marginTop: '4px',
    marginBottom: '4px',
  },
  dateSubtitle: {
    fontSize: 14,
    margin: 0,
  },
  dateOther: {
    color: '#254FAB',
    margin: 0,
    fontSize: 12,
    marginTop: -2,
  },
  selectedOther: {
    fontWeight: 400,
    fontSize: 14,
    flex: 1,
    textAlign: 'left',
  },
  buttonSelectedOther: {
    fontWeight: 700,
    fontSize: 14,
    color: '#254FAB',
    background: 'transparent',
    border: 0,
  },
  buttonClose: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 24,
    height: 24,
    background: 'transparent',
    border: 0,
    padding: 0,
  },
});

interface Props {
  selectedDate: Partial<Schedule>;
  setSelectedDate: (schedule: Partial<Schedule>) => void;
  schedules: Schedule[];
}

const DATE_FORMAT = 'YYYY-MM-DD';
const SELECTED_DATE_FORMAT = 'dddd, D MMMM YYYY';

const DateSection = ({ setSelectedDate, selectedDate, schedules }: Props) => {
  const { translate, currentLanguage } = useTranslate();
  const style = useStyles();
  const [isOpenDatePicker, setOpenDatePicker] = useState(false);
  const today = new Date();
  const maxDayBooking = moment().add(14, 'days');
  const recommendedDates = schedules.slice(0, 3).map(obj => obj.date);

  const allScheduleDates = useMemo(() => schedules.map(obj => obj.date), [
    schedules,
  ]);

  const isSelectOther = selectedDate
    ? !recommendedDates.includes(selectedDate?.date || '')
    : false;

  const handleChangeCalendar = (date?: string) => {
    if (date) {
      const formattedDate = moment(date).format(DATE_FORMAT);
      setSelectedDate({
        date: formattedDate,
        is_with_appointment: schedules.find(s => s.date === formattedDate)
          ?.is_with_appointment,
      });
      setTimeout(() => {
        setOpenDatePicker(false);
      }, 100);
    }
  };

  useEffect(() => {
    // NOTE: select first date
    if (!selectedDate?.date && recommendedDates[0]) {
      setSelectedDate({
        date: recommendedDates[0],
        is_with_appointment: !!schedules.find(
          s => s.date === recommendedDates[0]
        )?.is_with_appointment,
      });
    }
  }, [recommendedDates]);

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        mt={2}
        style={{
          gap: 16,
        }}
        textAlign="center"
      >
        {isSelectOther ? (
          <>
            <img src={calendarImage} alt="Tanggal" />
            <p className={style.selectedOther}>
              {moment(selectedDate?.date).format(SELECTED_DATE_FORMAT)}
            </p>
            <button
              className={style.buttonSelectedOther}
              type="button"
              onClick={() => setOpenDatePicker(!isOpenDatePicker)}
            >
              {translate('change')}
            </button>
          </>
        ) : (
          <>
            {recommendedDates.map(date => {
              const isSelected = date === selectedDate.date;
              return (
                <SelectableBox
                  onClick={() => {
                    setSelectedDate({
                      date,
                      is_with_appointment: schedules.find(s => s.date === date)
                        ?.is_with_appointment,
                    });
                  }}
                  isSelected={isSelected}
                  key={date}
                >
                  <p className={style.dateTitle}>
                    {moment(date).format('ddd')}
                  </p>
                  <p
                    className={style.dateSubtitle}
                    style={{
                      fontWeight: isSelected ? '700' : '400',
                    }}
                  >
                    {moment(date).format('DD MMM')}
                  </p>
                </SelectableBox>
              );
            })}
            <SelectableBox
              onClick={() => {
                setOpenDatePicker(!isOpenDatePicker);
              }}
              isSelected={false}
            >
              <img src={calendarImage} alt="Tanggal" />
              <p className={style.dateOther}>{translate('others')}</p>
            </SelectableBox>
          </>
        )}
      </Box>
      <Drawer
        anchor="bottom"
        open={isOpenDatePicker}
        onClose={() => {
          setOpenDatePicker(false);
        }}
        PaperProps={{
          style: {
            borderRadius: '24px 24px 0px 0px',
          },
        }}
      >
        <Box
          position="relative"
          textAlign="center"
          borderBottom="1px solid #EEF0F0"
        >
          <Box
            position="absolute"
            top={6}
            left="50%"
            marginLeft="-20px"
            bgcolor="#D8D8DF"
            borderRadius={2}
            width="40px"
            height="4px"
          />
          <p className="pt-8 font-weight-800 font-size-18">
            {translate('selectConsultationDate')}
          </p>
          <button
            type="button"
            onClick={() => setOpenDatePicker(false)}
            className={style.buttonClose}
          >
            <img src={closeImage} alt="close" />
          </button>
        </Box>

        <Calendar
          onChange={date => {
            handleChangeCalendar(date?.toString());
          }}
          value={selectedDate?.date}
          next2Label={null}
          prev2Label={null}
          navigationLabel={({ label }) => {
            const labels = label.split(' ');
            if (labels[0]) {
              return labels[0];
            }
            return label;
          }}
          selectRange={false}
          tileDisabled={tile =>
            !allScheduleDates.includes(moment(tile.date).format(DATE_FORMAT))
          }
          className="schedule-select"
          locale={currentLanguage === 'id' ? 'id-ID' : 'en-US'}
          minDate={today}
          maxDate={maxDayBooking.toDate()}
        />
      </Drawer>
    </>
  );
};

export default DateSection;
